/**
 * Share Card — Studio for exporting a polished trade card PNG.
 *
 * Source-of-truth design: a single `<canvas>` is drawn into on every
 * input change and shown directly in the preview pane. The exported
 * file comes from the same canvas (`canvas.toBlob`) so preview and
 * export are pixel-identical.
 */

import { useTheme } from "next-themes";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { CoolIcon } from "@/components/ui/cool-icon";
import { PageHeader } from "@/components/page-header";
import { DashboardSection } from "@/components/dashboard-section";
import { useSession } from "@/lib/auth-client";
import { displayName } from "@/lib/identity";
import {
  listAccounts,
  getAccountTrades,
  type AccountRow,
  type AccountTrade,
} from "@/lib/api/accounts";
import { listPairs } from "@/lib/api/pairs";
import { fmtMoney } from "@/lib/format";
import {
  CARD_H,
  CARD_W,
  getAccentFromCss,
  loadGeistFonts,
  loadImage,
  renderTradeCard,
  type ShareTrade,
} from "./canvas";
import { qrToImage } from "./qr";

const QR_TARGET = "https://pfh.trading";
const LOGO_URL = "/pfh-logo.png";
const TEMPLATE_URL = "/share-card-template.png";

type Source = "live" | "closed" | "manual";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function msToInput(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** AccountRow.phase is "step1" | "step2" | "step3" | "funded". Anything
 *  that isn't "funded" is an evaluation challenge — no real payout. */
function phaseToKind(phase: string | null | undefined): "evaluation" | "funded" {
  return phase === "funded" ? "funded" : "evaluation";
}

function defaultPayout(account: AccountRow | null): {
  accountKind: "evaluation" | "funded";
  payoutPct: number;
} {
  const kind = phaseToKind(account?.phase);
  if (kind === "evaluation") return { accountKind: "evaluation", payoutPct: 0 };
  return {
    accountKind: "funded",
    payoutPct: account?.profit_split_pct ?? 80,
  };
}

/** A currently-open trade row joined to its account + live leg PnL. */
interface OpenPosition {
  trade: AccountTrade;
  account: AccountRow;
  /** Live open-PnL for this leg pulled from the HedgePair DO via /api/pairs.
   *  Null when the DO is unreachable or the trade isn't bound to a pair yet. */
  livePnl: number | null;
}

function tradeFromOpen(p: OpenPosition): Omit<ShareTrade, "trader"> {
  return {
    status: "Open Trade",
    ...defaultPayout(p.account),
    symbol: p.trade.symbol,
    side: p.trade.side,
    lots: p.trade.lots,
    profit: p.livePnl ?? 0,
    openPrice: p.trade.entry_price,
    closePrice: null,
    openedAt: msToInput(p.trade.opened_at),
    closedAt: "",
  };
}

function tradeFromClosed(
  t: AccountTrade,
  account: AccountRow | null,
): Omit<ShareTrade, "trader"> {
  return {
    status: "Closed Trade",
    ...defaultPayout(account),
    symbol: t.symbol,
    side: t.side,
    lots: t.lots,
    profit: t.pnl ?? 0,
    openPrice: t.entry_price,
    closePrice: t.close_price,
    openedAt: msToInput(t.opened_at),
    closedAt: msToInput(t.closed_at ?? 0),
  };
}

function defaultManual(): Omit<ShareTrade, "trader"> {
  return {
    status: "Closed Trade",
    accountKind: "funded",
    payoutPct: 80,
    symbol: "XAUUSD",
    side: "sell",
    lots: 0.05,
    profit: 75.4,
    openPrice: 4756.31,
    closePrice: 4726.12,
    openedAt: "2026-04-22T07:44",
    closedAt: "2026-04-22T19:21",
  };
}

function openKey(p: OpenPosition): string {
  return `${p.account.id}:${p.trade.ticket}`;
}

function accountLabel(a: AccountRow): string {
  const nick = a.account_label?.trim();
  const size = a.account_size
    ? `${(a.account_size / 1000).toFixed(0)}k`
    : null;
  const tail = [a.firm_id, size].filter(Boolean).join(" · ");
  return nick ? `${nick} · ${tail}` : tail;
}

export function ShareCardPage(): ReactNode {
  const { data: session } = useSession();
  const sessionTrader = displayName(
    session?.user?.name,
    session?.user?.email,
  );

  // --- trader name (defaults to session name; tracked-as-touched so live picks
  //     don't clobber the user's typed override).
  const [trader, setTraderRaw] = useState<string>("");
  const traderTouched = useRef(false);
  useEffect(() => {
    if (traderTouched.current) return;
    if (!session?.user) return;
    setTraderRaw(sessionTrader);
  }, [session?.user, sessionTrader]);
  const setTrader = useCallback((value: string): void => {
    traderTouched.current = true;
    setTraderRaw(value);
  }, []);
  const resetTrader = useCallback((): void => {
    traderTouched.current = false;
    setTraderRaw(sessionTrader);
  }, [sessionTrader]);
  const traderEdited =
    traderTouched.current && trader.trim() !== sessionTrader.trim();

  // --- source segment
  const [source, setSource] = useState<Source>("manual");

  // --- accounts list (used by both live + closed pickers to resolve
  //      account.phase / profit_split_pct so the card auto-fills the
  //      correct evaluation/funded badge + take-home math).
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: listAccounts,
    staleTime: 60_000,
  });
  const accountsById = useMemo(() => {
    const m = new Map<string, AccountRow>();
    for (const a of accountsQuery.data ?? []) m.set(a.id, a);
    return m;
  }, [accountsQuery.data]);

  // --- live positions: fan out trades-per-active-account in parallel and
  //     keep only rows that haven't closed yet. The dashboard store would
  //     have these too, but only when a pair-detail page is mounted (the
  //     hedge socket is per-pair, not global), so on /share-card we go
  //     straight to the API. Polled lightly so a freshly-opened position
  //     shows up without a manual refresh.
  const activeAccounts = useMemo(
    () => (accountsQuery.data ?? []).filter((a) => a.status === "active"),
    [accountsQuery.data],
  );
  const openTradeQueries = useQueries({
    queries: activeAccounts.map((a) => ({
      queryKey: ["account-open-trades", a.id],
      queryFn: () => getAccountTrades(a.id),
      staleTime: 10_000,
      refetchInterval: 15_000,
    })),
  });
  // Live per-leg PnL via /api/pairs (DO state). Used as the prefilled
  // profit when the operator picks an open position. Refreshed on the
  // same cadence as open trades so the share card mirrors what the
  // operator sees on the pair-detail page.
  const pairsQuery = useQuery({
    queryKey: ["pairs"],
    queryFn: listPairs,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
  const legPnlByAccount = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pairsQuery.data ?? []) {
      m.set(p.longAccount.accountId, p.longAccount.pnl);
      m.set(p.shortAccount.accountId, p.shortAccount.pnl);
    }
    return m;
  }, [pairsQuery.data]);
  const openPositions = useMemo<OpenPosition[]>(() => {
    const out: OpenPosition[] = [];
    openTradeQueries.forEach((q, i) => {
      const account = activeAccounts[i];
      if (!account || !q.data) return;
      for (const t of q.data) {
        if (t.closed_at != null) continue;
        out.push({
          trade: t,
          account,
          livePnl: legPnlByAccount.get(account.id) ?? null,
        });
      }
    });
    return out.sort((a, b) => b.trade.opened_at - a.trade.opened_at);
  }, [openTradeQueries, activeAccounts, legPnlByAccount]);
  const openTradesLoading = openTradeQueries.some((q) => q.isLoading);

  // --- closed-trade picker (account select → trade select)
  const [closedAccountId, setClosedAccountId] = useState<string>("");
  const closedTradesQuery = useQuery({
    queryKey: ["account-trades", closedAccountId],
    queryFn: () => getAccountTrades(closedAccountId),
    enabled: closedAccountId !== "",
    staleTime: 30_000,
  });
  const closedTrades = useMemo(() => {
    return (closedTradesQuery.data ?? [])
      .filter((t) => t.closed_at != null && t.pnl != null)
      .sort((a, b) => (b.closed_at ?? 0) - (a.closed_at ?? 0));
  }, [closedTradesQuery.data]);

  // --- the trade currently being rendered (everything except trader name)
  const [base, setBase] = useState<Omit<ShareTrade, "trader">>(() =>
    defaultManual(),
  );
  const [livePicked, setLivePicked] = useState<string>("");
  const [closedPicked, setClosedPicked] = useState<string>("");
  const update = useCallback(
    <K extends keyof Omit<ShareTrade, "trader">>(
      key: K,
      value: Omit<ShareTrade, "trader">[K],
    ): void => {
      setBase((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const trade: ShareTrade = useMemo(
    () => ({ ...base, trader: trader.trim() || sessionTrader }),
    [base, trader, sessionTrader],
  );

  // --- canvas + assets
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [bg, setBg] = useState<HTMLImageElement | null>(null);
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [qr, setQr] = useState<HTMLImageElement | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [accent, setAccent] = useState<string>(() =>
    typeof window === "undefined"
      ? "oklch(0.718 0.202 349.761)"
      : getAccentFromCss(),
  );

  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const id = window.requestAnimationFrame(() =>
      setAccent(getAccentFromCss()),
    );
    return () => window.cancelAnimationFrame(id);
  }, [resolvedTheme]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [bgImg, logoImg, qrImg] = await Promise.all([
        loadImage(TEMPLATE_URL),
        loadImage(LOGO_URL),
        qrToImage(QR_TARGET),
      ]);
      if (cancelled) return;
      setBg(bgImg);
      setLogo(logoImg);
      setQr(qrImg);
    })();
    void loadGeistFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const ready =
    fontsReady && bg !== null && logo !== null && qr !== null;

  useEffect(() => {
    if (!canvasRef.current || !ready || !bg || !logo || !qr) return;
    renderTradeCard(canvasRef.current, trade, { bg, logo, qr, accent });
  }, [trade, ready, bg, logo, qr, accent]);

  const onPickLive = (id: string): void => {
    setLivePicked(id);
    const p = openPositions.find((x) => openKey(x) === id);
    if (p) setBase(tradeFromOpen(p));
  };
  const onPickClosed = (id: string): void => {
    setClosedPicked(id);
    const t = closedTrades.find((x) => x.id === id);
    if (t)
      setBase(
        tradeFromClosed(t, accountsById.get(t.account_id) ?? null),
      );
  };

  const download = (): void => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pfh-${trade.symbol.toLowerCase() || "trade"}-${trade.status
        .toLowerCase()
        .replace(/\s+/g, "-")}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Share Your Wins"
        subtitle="Pick a trade, fine-tune the details, export a polished PNG to share anywhere."
        icon={<CoolIcon name="Image_01" size={20} />}
        accentWords={["Wins"]}
        right={
          <Button onClick={download} disabled={!ready} className="gap-2">
            <CoolIcon name="Download" /> Export PNG
          </Button>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* ============== Form ============== */}
        <DashboardSection title="Compose Card">
          <div className="flex flex-col gap-5">
            {/* --- Source segment --- */}
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                Trade Source
              </Label>
              <ToggleGroup
                value={[source]}
                onValueChange={(v) => {
                  const next = v[0] as Source | undefined;
                  if (next) setSource(next);
                }}
                variant="outline"
                className="grid w-full grid-cols-3"
              >
                <ToggleGroupItem value="live" className="w-full">
                  <CoolIcon name="Trending_Up" size={14} />
                  Live
                </ToggleGroupItem>
                <ToggleGroupItem value="closed" className="w-full">
                  <CoolIcon name="Check_All" size={14} />
                  Closed
                </ToggleGroupItem>
                <ToggleGroupItem value="manual" className="w-full">
                  <CoolIcon name="Edit_Pencil_01" size={14} />
                  Manual
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {/* --- Source-specific picker --- */}
            {source === "live" && (
              <Field label="Open Position">
                {openTradesLoading && openPositions.length === 0 ? (
                  <Skeleton className="h-8 w-full" />
                ) : openPositions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No live positions on any account right now.
                  </p>
                ) : (
                  <Select
                    value={livePicked}
                    onValueChange={(v) => onPickLive(String(v))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pick a position" />
                    </SelectTrigger>
                    <SelectContent>
                      {openPositions.map((p) => (
                        <SelectItem key={openKey(p)} value={openKey(p)}>
                          {p.trade.symbol} · {p.trade.side.toUpperCase()} ·{" "}
                          {p.trade.lots}
                          {p.livePnl != null
                            ? ` · ${fmtMoney(p.livePnl)}`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            )}

            {source === "closed" && (
              <div className="flex flex-col gap-3">
                <Field label="Account">
                  <Select
                    value={closedAccountId}
                    onValueChange={(v) => {
                      setClosedAccountId(String(v));
                      setClosedPicked("");
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pick an account" />
                    </SelectTrigger>
                    <SelectContent>
                      {(accountsQuery.data ?? []).map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {accountLabel(a)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {closedAccountId && (
                  <Field label="Closed Trade">
                    {closedTradesQuery.isLoading ? (
                      <Skeleton className="h-8 w-full" />
                    ) : closedTrades.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No closed trades on this account yet.
                      </p>
                    ) : (
                      <Select
                        value={closedPicked}
                        onValueChange={(v) => onPickClosed(String(v))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Pick a closed trade" />
                        </SelectTrigger>
                        <SelectContent>
                          {closedTrades.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.symbol} · {t.side.toUpperCase()} ·{" "}
                              {t.lots} · {fmtMoney(t.pnl ?? 0)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                )}
              </div>
            )}

            <Separator />

            {/* --- Trader name --- */}
            <Field label="Trader Name">
              <div className="flex gap-2">
                <Input
                  value={trader}
                  onChange={(e) => setTrader(e.target.value)}
                  placeholder={sessionTrader}
                  maxLength={28}
                />
                {traderEdited && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={resetTrader}
                    aria-label="Reset trader name to your account default"
                  >
                    <CoolIcon name="Arrow_Reload_02" />
                  </Button>
                )}
              </div>
            </Field>

            {/* --- Account type / split --- */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Account Type">
                <Select
                  value={base.accountKind}
                  onValueChange={(v) =>
                    update(
                      "accountKind",
                      v as ShareTrade["accountKind"],
                    )
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="evaluation">Evaluation</SelectItem>
                    <SelectItem value="funded">Funded</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Profit Split (%)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  value={base.payoutPct}
                  onChange={(e) =>
                    update(
                      "payoutPct",
                      Math.max(0, Math.min(100, Number(e.target.value))),
                    )
                  }
                  disabled={base.accountKind === "evaluation"}
                />
              </Field>
            </div>

            <Separator />

            {/* --- Trade descriptive --- */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Status">
                <Select
                  value={base.status}
                  onValueChange={(v) =>
                    update("status", v as ShareTrade["status"])
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open Trade">Open Trade</SelectItem>
                    <SelectItem value="Closed Trade">
                      Closed Trade
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Side">
                <Select
                  value={base.side}
                  onValueChange={(v) =>
                    update("side", v as ShareTrade["side"])
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buy">Buy</SelectItem>
                    <SelectItem value="sell">Sell</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Symbol">
                <Input
                  value={base.symbol}
                  onChange={(e) =>
                    update("symbol", e.target.value.toUpperCase())
                  }
                  maxLength={12}
                />
              </Field>
              <Field label="Lots">
                <Input
                  type="number"
                  step="0.01"
                  value={base.lots}
                  onChange={(e) => update("lots", Number(e.target.value))}
                />
              </Field>
            </div>

            <Separator />

            {/* --- Trade financial --- */}
            <Field label="Profit ($)">
              <Input
                type="number"
                step="0.01"
                value={base.profit}
                onChange={(e) => update("profit", Number(e.target.value))}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Open Price">
                <Input
                  type="number"
                  step="0.01"
                  value={base.openPrice}
                  onChange={(e) =>
                    update("openPrice", Number(e.target.value))
                  }
                />
              </Field>
              <Field
                label={
                  base.status === "Closed Trade"
                    ? "Close Price"
                    : "Live Price"
                }
              >
                <Input
                  type="number"
                  step="0.01"
                  value={base.closePrice ?? base.openPrice}
                  onChange={(e) =>
                    update("closePrice", Number(e.target.value))
                  }
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Opened At">
                <Input
                  type="datetime-local"
                  value={base.openedAt}
                  onChange={(e) => update("openedAt", e.target.value)}
                />
              </Field>
              <Field label="Closed At">
                <Input
                  type="datetime-local"
                  value={base.closedAt}
                  onChange={(e) => update("closedAt", e.target.value)}
                  disabled={base.status === "Open Trade"}
                />
              </Field>
            </div>
          </div>
        </DashboardSection>

        {/* ============== Preview ============== */}
        <DashboardSection title="Preview">
          <div className="relative mx-auto aspect-square w-full max-w-[760px] overflow-hidden rounded-2xl border border-accent/30 bg-[#06061c] shadow-2xl shadow-accent/15">
            <canvas
              ref={canvasRef}
              width={CARD_W}
              height={CARD_H}
              className={`block h-full w-full transition-opacity duration-200 ${
                ready ? "opacity-100" : "opacity-0"
              }`}
            />
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Skeleton className="h-full w-full rounded-2xl" />
              </div>
            )}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
